import { HomeReview, ReviewResponse } from '../types';
import { OfflineResponse, OfflineReview } from './offlineDatabase';
import { serializeReviewItem } from './offlineReviewHydration';
import { dedupeResponsesByItemId } from './reviewResponseSelector';

export interface OfflineReviewCacheDatabase {
  getReview(reviewId: number): Promise<OfflineReview | null>;
  updateReview(reviewId: number, updates: Partial<OfflineReview>): Promise<void>;
  createReviewWithId(review: Partial<OfflineReview> & { id: number }): Promise<OfflineReview>;
  saveReviewTemplateSnapshot(reviewId: number, template: any): Promise<void>;
  getResponse(responseId: number): Promise<OfflineResponse | null>;
  getResponsesByReviewId(reviewId: number): Promise<OfflineResponse[]>;
  updateResponse(responseId: number, updates: Partial<OfflineResponse>): Promise<void>;
  updateResponseIfUnchanged?(responseId: number, expectedLocalRevision: number, updates: Partial<OfflineResponse>): Promise<boolean>;
  createResponseWithId(response: Partial<OfflineResponse> & { id: number }): Promise<OfflineResponse>;
  cachePhotoMetadata(metadata: any): Promise<void>;
  createRecoverySnapshot?(reviewId: number, reason: string): Promise<void>;
  recordOfflineMutation?(entry: {
    entityType: 'response';
    entityId?: number | null;
    reviewId?: number | null;
    itemId?: number | null;
    mutationType: string;
    payload?: unknown;
    previousSnapshot?: unknown;
    status?: 'pending' | 'synced' | 'failed' | 'conflict';
  }): Promise<number | undefined>;
  saveIdMapping?(tempId: string, realId: number, entityType: 'response'): Promise<void>;
}

export interface CacheOnlineReviewOptions {
  skipCache?: boolean;
  skipResponseCache?: boolean;
}

const recoverableConflictStatuses = new Set(['local_wins', 'conflicted', 'restored']);

export class OfflineReviewCacheService {
  constructor(
    private readonly database: OfflineReviewCacheDatabase | null | undefined,
    private readonly logger: Pick<Console, 'error' | 'warn'> = console
  ) {}

  async cacheOnlineReview(
    apiReview: HomeReview,
    options: CacheOnlineReviewOptions = {}
  ): Promise<void> {
    try {
      if (options.skipCache) {
        return;
      }

      if (!this.database) {
        this.logger.error('[OfflineReviewService] Skipping cache - database not available');
        return;
      }

      let existingOfflineReview: OfflineReview | null = null;
      try {
        existingOfflineReview = await this.database.getReview(apiReview.id);
      } catch (existingError) {
        existingOfflineReview = null;
      }

      const hasPendingReviewChanges = existingOfflineReview &&
        (existingOfflineReview.syncStatus === 'pending' ||
          existingOfflineReview.syncStatus === 'failed' ||
          existingOfflineReview.syncStatus === 'syncing');

      const notesToCache = hasPendingReviewChanges && existingOfflineReview?.notes !== undefined
        ? existingOfflineReview.notes
        : apiReview.notes;

      const onDemandProjectsToCache = hasPendingReviewChanges && existingOfflineReview?.onDemandProjects !== undefined
        ? existingOfflineReview.onDemandProjects
        : apiReview.onDemandProjects;

      const vendorNotesToCache = hasPendingReviewChanges && existingOfflineReview?.vendorNotes !== undefined
        ? existingOfflineReview.vendorNotes
        : apiReview.vendorNotes;

      const memberNameToCache = hasPendingReviewChanges && existingOfflineReview?.memberName !== undefined
        ? existingOfflineReview.memberName
        : apiReview.memberName;
      const memberEmailToCache = hasPendingReviewChanges && existingOfflineReview?.memberEmail !== undefined
        ? existingOfflineReview.memberEmail
        : apiReview.memberEmail;
      const homeManagerNameToCache = hasPendingReviewChanges && existingOfflineReview?.homeManagerName !== undefined
        ? existingOfflineReview.homeManagerName
        : apiReview.homeManagerName;
      const homeManagerEmailToCache = hasPendingReviewChanges && existingOfflineReview?.homeManagerEmail !== undefined
        ? existingOfflineReview.homeManagerEmail
        : apiReview.homeManagerEmail;

      if (hasPendingReviewChanges && existingOfflineReview) {
        await this.database.updateReview(apiReview.id, {
          status: apiReview.status,
          completedAt: apiReview.completedAt ? new Date(apiReview.completedAt) : undefined,
          submittedAt: apiReview.submittedAt ? new Date(apiReview.submittedAt) : undefined,
          notes: notesToCache,
          onDemandProjects: onDemandProjectsToCache,
          vendorNotes: vendorNotesToCache,
          memberName: memberNameToCache,
          memberEmail: memberEmailToCache,
          homeManagerName: homeManagerNameToCache,
          homeManagerEmail: homeManagerEmailToCache,
          metadata: apiReview.metadata,
        });
      } else {
        await this.database.createReviewWithId({
          id: apiReview.id,
          tempId: existingOfflineReview?.tempId,
          propertyId: apiReview.propertyId,
          templateId: apiReview.templateId,
          reviewerId: apiReview.reviewerId,
          status: apiReview.status,
          startedAt: apiReview.startedAt,
          completedAt: apiReview.completedAt,
          submittedAt: apiReview.submittedAt,
          notes: notesToCache,
          onDemandProjects: onDemandProjectsToCache,
          vendorNotes: vendorNotesToCache,
          address: apiReview.address || apiReview.property?.address,
          memberName: memberNameToCache,
          memberEmail: memberEmailToCache,
          homeManagerName: homeManagerNameToCache,
          homeManagerEmail: homeManagerEmailToCache,
          metadata: apiReview.metadata,
          syncStatus: 'synced',
          createdAt: existingOfflineReview?.createdAt || apiReview.createdAt,
          updatedAt: new Date(),
        });
      }

      await this.cacheTemplateSnapshot(apiReview);
      if (!options.skipResponseCache && Array.isArray(apiReview.responses)) {
        await this.cacheResponses(apiReview);
      }
    } catch (error) {
      this.logger.error('[OfflineReviewService] Failed to cache online review:', error);
    }
  }

  private async cacheTemplateSnapshot(apiReview: HomeReview): Promise<void> {
    if (!this.database || !apiReview.template) {
      return;
    }

    try {
      await this.database.saveReviewTemplateSnapshot(apiReview.id, apiReview.template);
    } catch (snapshotError) {
      this.logger.error('[OfflineReviewService] Failed to cache template snapshot:', snapshotError);
    }
  }

  private async cacheResponses(apiReview: HomeReview): Promise<void> {
    if (!this.database) {
      return;
    }

    const localResponses = await this.database.getResponsesByReviewId(apiReview.id).catch((): OfflineResponse[] => []);
    const localById = new Map<number, OfflineResponse>(
      localResponses.map((response): [number, OfflineResponse] => [response.id, response])
    );
    const localByItemId = new Map<number, OfflineResponse>();
    for (const localResponse of localResponses) {
      if (localResponse.itemId != null && !localByItemId.has(localResponse.itemId)) {
        localByItemId.set(localResponse.itemId, localResponse);
      }
    }

    const serverItemIds = new Set<number>();

    const serverResponses = dedupeResponsesByItemId((apiReview.responses || []) as ReviewResponse[]);

    for (const response of serverResponses) {
      try {
        serverItemIds.add(response.itemId);
        const existingResponse = localById.get(response.id)
          || (response.itemId != null ? localByItemId.get(response.itemId) : undefined)
          || await this.database.getResponse(response.id);

        if (
          existingResponse &&
          (
            existingResponse.conflictStatus === 'conflicted' ||
            (
              recoverableConflictStatuses.has(existingResponse.conflictStatus || '') &&
              !this.isActiveSyncStatus(existingResponse.syncStatus)
            )
          )
        ) {
          await this.cacheResponsePhotos(response);
          continue;
        }

        if (existingResponse && this.shouldPreserveLocalResponse(existingResponse, response)) {
          const isActiveLocalSync = this.isActiveSyncStatus(existingResponse.syncStatus);
          const conflictStatus = isActiveLocalSync
            ? existingResponse.conflictStatus === 'server_wins' ? 'server_wins' : 'none'
            : 'local_wins';
          await this.database.updateResponse(existingResponse.id, {
            syncStatus: existingResponse.syncStatus === 'syncing' ? 'syncing' : 'pending',
            conflictStatus,
            lastServerSeenAt: response.updatedAt ? new Date(response.updatedAt) : new Date(),
            itemSnapshot: existingResponse.itemSnapshot || serializeReviewItem((response as any).item),
          });
          if (!isActiveLocalSync) {
            await this.database.recordOfflineMutation?.({
              entityType: 'response',
              entityId: existingResponse.id,
              reviewId: apiReview.id,
              itemId: existingResponse.itemId ?? response.itemId,
              mutationType: 'cache_refresh_preserved_local_response',
              payload: {
                serverResponseId: response.id,
                serverUpdatedAt: response.updatedAt,
                localUpdatedAt: existingResponse.updatedAt,
                localStatus: existingResponse.status,
                serverStatus: response.status,
              },
              previousSnapshot: existingResponse,
              status: 'pending',
            });
            await this.database.createRecoverySnapshot?.(apiReview.id, 'cache_refresh_local_winner');
          }
          this.logger.warn('[OfflineReviewCacheService] Preserved local response during cache refresh:', {
            reviewId: apiReview.id,
            itemId: response.itemId,
            localResponseId: existingResponse.id,
            serverResponseId: response.id,
            localUpdatedAt: existingResponse.updatedAt,
            serverUpdatedAt: response.updatedAt,
            localStatus: existingResponse.status,
            serverStatus: response.status,
            winner: 'local',
          });
          await this.cacheResponsePhotos(response);
          continue;
        }

        if (
          existingResponse &&
          existingResponse.id !== response.id &&
          existingResponse.tempId &&
          this.database.saveIdMapping
        ) {
          await this.database.saveIdMapping(existingResponse.tempId, response.id, 'response');
        }

        if (existingResponse) {
          const didApplyServerWinner = await this.applyServerResponseIfUnchanged(
            existingResponse,
            response
          );
          if (!didApplyServerWinner) {
            this.logger.warn('[OfflineReviewCacheService] Skipped stale server cache response because local response changed during refresh:', {
              reviewId: apiReview.id,
              itemId: response.itemId,
              localResponseId: existingResponse.id,
              serverResponseId: response.id,
            });
            continue;
          }

          await this.cacheResponsePhotos(response);
          continue;
        }

        await this.database.createResponseWithId({
          id: response.id,
          reviewId: apiReview.id,
          itemId: response.itemId,
          roomId: response.roomId,
          floor: response.floor,
          isPresent: response.isPresent,
          status: response.status,
          responseData: response.responseData,
          notes: response.notes,
          findings: response.findings || [],
          syncStatus: 'synced',
          createdAt: response.createdAt,
          updatedAt: response.updatedAt,
          itemSnapshot: serializeReviewItem((response as any).item),
        });

        await this.cacheResponsePhotos(response);
      } catch (responseError) {
        this.logger.error('[OfflineReviewService] Failed to cache response', response.id, ':', responseError);
      }
    }

    for (const localResponse of localResponses) {
      if (
        localResponse.itemId == null ||
        serverItemIds.has(localResponse.itemId) ||
        recoverableConflictStatuses.has(localResponse.conflictStatus || '') ||
        localResponse.conflictStatus === 'server_wins' ||
        localResponse.syncStatus !== 'synced'
      ) {
        continue;
      }

      const localStatusPriority = this.statusPriority(localResponse.status);
      const hasMeaningfulLocalContent =
        this.hasMeaningfulResponseData(localResponse.responseData) ||
        this.hasMeaningfulText(localResponse.notes) ||
        Boolean(localResponse.findings?.length);
      if (localStatusPriority <= 0 && !hasMeaningfulLocalContent) {
        continue;
      }

      await this.database.updateResponse(localResponse.id, {
        syncStatus: 'pending',
        conflictStatus: 'local_wins',
        lastServerSeenAt: new Date(),
      });
      await this.database.recordOfflineMutation?.({
        entityType: 'response',
        entityId: localResponse.id,
        reviewId: apiReview.id,
        itemId: localResponse.itemId,
        mutationType: 'cache_refresh_recovered_orphan_response',
        payload: {
          localUpdatedAt: localResponse.updatedAt,
          localStatus: localResponse.status,
        },
        previousSnapshot: localResponse,
        status: 'pending',
      });
      await this.database.createRecoverySnapshot?.(apiReview.id, 'cache_refresh_orphan_response');
      this.logger.warn('[OfflineReviewCacheService] Recovered local response missing from server cache payload:', {
        reviewId: apiReview.id,
        itemId: localResponse.itemId,
        localResponseId: localResponse.id,
        localUpdatedAt: localResponse.updatedAt,
        localStatus: localResponse.status,
        winner: 'local',
      });
    }
  }

  private shouldPreserveLocalResponse(localResponse: OfflineResponse, serverResponse: any): boolean {
    if (localResponse.conflictStatus === 'server_wins') {
      return false;
    }

    if (this.matchesServerResponse(localResponse, serverResponse)) {
      return false;
    }

    const localStatusPriority = this.statusPriority(localResponse.status);
    const serverStatusPriority = this.statusPriority(serverResponse.status);
    if (serverResponse.status === 'completed' && localResponse.status !== 'completed') {
      return false;
    }

    const localTimestamp = this.timestampMs(localResponse.lastLocalMutationAt || localResponse.updatedAt);
    const serverTimestamp = this.timestampMs(serverResponse.updatedAt);
    const hasComparableTimestamps = localTimestamp != null && serverTimestamp != null;

    if (hasComparableTimestamps) {
      if (serverTimestamp > localTimestamp) {
        return false;
      }

      if (localTimestamp > serverTimestamp) {
        return true;
      }
    }

    if (
      localResponse.syncStatus === 'pending' ||
      localResponse.syncStatus === 'failed' ||
      localResponse.syncStatus === 'syncing' ||
      localResponse.conflictStatus === 'local_wins' ||
      localResponse.conflictStatus === 'restored'
    ) {
      return true;
    }

    if (localStatusPriority > serverStatusPriority) {
      return true;
    }

    return !hasComparableTimestamps && localStatusPriority > 0 && serverStatusPriority === 0;
  }

  private isActiveSyncStatus(syncStatus: unknown): boolean {
    return syncStatus === 'pending' || syncStatus === 'syncing';
  }

  private async applyServerResponseIfUnchanged(
    existingResponse: OfflineResponse,
    serverResponse: ReviewResponse
  ): Promise<boolean> {
    const isServerAckForLocalData = this.matchesServerResponse(existingResponse, serverResponse);
    const updates: Partial<OfflineResponse> = {
      itemId: serverResponse.itemId,
      roomId: serverResponse.roomId ?? null as any,
      floor: serverResponse.floor ?? null as any,
      isPresent: serverResponse.isPresent,
      status: serverResponse.status,
      responseData: serverResponse.responseData,
      notes: (serverResponse as any).notes ?? null,
      findings: serverResponse.findings || [],
      syncStatus: 'synced',
      syncError: null,
      lastServerSeenAt: serverResponse.updatedAt ? new Date(serverResponse.updatedAt) : new Date(),
      serverRevision: (existingResponse.serverRevision ?? 0) + 1,
      conflictStatus: isServerAckForLocalData ? 'none' : 'server_wins',
      itemSnapshot: serializeReviewItem((serverResponse as any).item),
    };

    if (this.database?.updateResponseIfUnchanged) {
      return this.database.updateResponseIfUnchanged(
        existingResponse.id,
        existingResponse.localRevision ?? 0,
        updates
      );
    }

    await this.database!.updateResponse(existingResponse.id, updates);
    return true;
  }

  private statusPriority(status: unknown): number {
    if (status === 'completed') return 2;
    if (status === 'in_progress') return 1;
    return 0;
  }

  private hasMeaningfulText(value: unknown): boolean {
    return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
  }

  private hasMeaningfulResponseData(value: unknown): boolean {
    if (value == null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return Boolean(value);
  }

  private timestampMs(value: unknown): number | null {
    if (!value) {
      return null;
    }

    const time = new Date(value as any).getTime();
    return Number.isFinite(time) ? time : null;
  }

  private matchesServerResponse(localResponse: OfflineResponse, serverResponse: ReviewResponse): boolean {
    return (
      localResponse.status === serverResponse.status &&
      Boolean(localResponse.isPresent) === Boolean(serverResponse.isPresent) &&
      this.normalizedText(localResponse.notes) === this.normalizedText((serverResponse as any).notes) &&
      this.stableJson(localResponse.responseData) === this.stableJson(serverResponse.responseData) &&
      this.stableJson(localResponse.findings || []) === this.stableJson(serverResponse.findings || [])
    );
  }

  private normalizedText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private stableJson(value: unknown): string {
    if (value == null) {
      return '';
    }

    return JSON.stringify(this.sortJson(value));
  }

  private sortJson(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(entry => this.sortJson(entry));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = this.sortJson((value as Record<string, unknown>)[key]);
          return acc;
        }, {});
    }

    return value;
  }

  private async cacheResponsePhotos(response: any): Promise<void> {
    if (!this.database || !response.photos || response.photos.length === 0) {
      return;
    }

    for (const photo of response.photos) {
      try {
        await this.database.cachePhotoMetadata({
          id: photo.id,
          responseId: response.id,
          s3Key: photo.s3Key,
          s3Url: photo.s3Url,
          fileName: photo.fileName,
          mimeType: photo.mimeType,
          size: photo.size,
          caption: photo.caption,
          note: photo.note,
          metadata: photo.metadata ? JSON.stringify(photo.metadata) : undefined,
          questionId: photo.metadata?.questionId,
          uploadedAt: photo.uploadedAt,
        });
      } catch (photoError) {
        this.logger.error('[OfflineReviewService] Failed to cache photo metadata', photo.id, ':', photoError);
      }
    }
  }
}
